/**
 * The one place we decide whether a programmatic scroll animates.
 *
 * Two independent reasons to freeze it:
 *  - `prefers-reduced-motion: reduce` — the user asked for no motion.
 *  - `navigator.webdriver` — Happo/Playwright. A smooth scroll is still
 *    mid-flight when the screenshot is taken, so the captured offset is
 *    whatever the animation happened to reach: a snapshot that diffs against
 *    itself run to run. (testing.md, "Happo flake sources".)
 *
 * Extracted from BoardChipTabs when the chip-add jump made BoardLayout's feed
 * scroll fire automatically instead of only on a user tap — two copies of a
 * determinism gate is how one of them silently drifts.
 */
/**
 * "Never animate, whatever the CSS says."
 *
 * NOT interchangeable with `'auto'`. `'auto'` means *defer to the element's
 * computed `scroll-behavior`* — and this app loads Bootstrap's reboot, which
 * ships `@media (prefers-reduced-motion: no-preference) { :root {
 * scroll-behavior: smooth } }`. So on the document scroller every `'auto'`
 * scroll animates for everyone who has not asked for reduced motion, and
 * `'auto'` reads like "instant" in review while behaving like "smooth" in
 * production.
 *
 * That has now bitten twice: Kate's "it animates all the way down" on the PDP
 * return anchor (2026-07-28), and the THE-567 Refresh-board pill taking
 * seconds to crawl to the profile card (board, 2026-09-25). Both are
 * convergence loops, and smooth is especially bad for those — the loop reads
 * the offset back synchronously, the browser has not moved yet, so every frame
 * re-issues a fresh animation toward a target it never observes itself
 * reaching. Use this for any measure-scroll-remeasure loop.
 *
 * `'instant'` is CSSOM-spec'd (Chrome 97+, Safari 15.4+, Firefox 104+) but
 * this TypeScript lib.dom still types ScrollBehavior as `'auto' | 'smooth'`,
 * hence the cast. Safe against our floor: index.css already requires Safari
 * 16+ (`overflow-x: clip`), well above where `'instant'` landed.
 */
export const INSTANT_SCROLL = 'instant' as unknown as ScrollBehavior;

export function scrollBehavior(): ScrollBehavior {
  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const automated =
    typeof navigator !== 'undefined' && Boolean(navigator.webdriver);
  return prefersReducedMotion || automated ? 'auto' : 'smooth';
}
